import type { Product } from '@/models/ProductTypes';

const PRODUCT_PATH = '/vefverslun/um-voru';

// Resolves a requested SKU against the product's real variants; unknown values fall back to the default.
export const resolveVariant = (product: Product | null, requestedSku: string) => {
  const fallback = { sku: product?.productId ?? '', label: null as string | null };
  if (!product || !requestedSku) return fallback;

  const colors = product.productVariants?.variants ?? [];
  for (const color of colors) {
    const sub = color.subVariants?.variants?.find((v) => v.variantProductId === requestedSku);
    if (sub) {
      const parts = [colors.length > 1 ? color.variantName : null, sub.variantProperty].filter(Boolean);
      return { sku: requestedSku, label: parts.length > 0 ? parts.join(', ') : null };
    }
  }
  return fallback;
};

/** Path to a product page, selecting the variation `sku` when one is given. */
export const productPath = (productId: string, sku?: string) => {
  const base = `${PRODUCT_PATH}/${encodeURIComponent(productId)}`;
  return sku ? `${base}?sku=${encodeURIComponent(sku)}` : base;
};

/**
 * The upstream API resolves a variation SKU used as the slug to its parent product. When that happens,
 * returns the canonical `/um-voru/<parent>?sku=<variation>` path so the requested variation isn't lost.
 * Returns null when the slug already is the product's own id.
 */
export const variationRedirectPath = (product: Product | null, slug: string, requestedSku: string): string | null => {
  if (!product?.productId || slug === product.productId) return null;
  const { sku } = resolveVariant(product, requestedSku || slug);
  return productPath(product.productId, sku !== product.productId ? sku : undefined);
};
